import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import swal from "sweetalert";
import Loading from "../../components/loading";
import Pagination from "../../components/Pagination";
import {
    AKFA_PAGE_BASE,
    approveAkfaMappingPrice,
    bulkApproveAkfaMappingPrice,
    bulkRejectAkfaMappingPrice,
    getAkfaMappings,
    rejectAkfaMappingPrice,
} from "../../services/akfa/akfaService";
import {
    AKFA_PAGE_SIZE,
    SELECT_CLASS,
    formatDateTime,
    formatPrice,
    showError,
    showSuccess,
    toPageState,
} from "./akfaUtils";

const BIG_DELTA_THRESHOLD = 15;
const PRICE_STATUSES = ["PENDING", "ERROR", "SYNCED", "REJECTED", "NO_PRICE", "UNSUPPORTED"];
const MIN_DELTA_OPTIONS = [5, 10, 20];

const PRICE_STATUS_STYLES = {
    PENDING: "bg-indigo-100 text-indigo-800",
    SYNCED: "bg-green-100 text-green-800",
    REJECTED: "bg-gray-200 text-gray-600",
    ERROR: "bg-red-100 text-red-800",
    NO_PRICE: "bg-yellow-100 text-yellow-800",
    UNSUPPORTED: "bg-gray-300 text-gray-700",
};

function DeltaBadge({ delta }) {
    if (delta === null || delta === undefined) return <span className="text-gray-400">—</span>;
    const up = delta > 0;
    const big = Math.abs(delta) >= BIG_DELTA_THRESHOLD;
    return (
        <span
            className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded ${
                up ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
            }`}
        >
            {big && "⚠ "}
            {up ? "▲" : "▼"}
            {Math.abs(delta).toFixed(1)}%
        </span>
    );
}

export default function AkfaPriceChangesTab({ refreshSummary }) {
    const { t } = useTranslation();
    const { token } = useSelector((state) => state.user);

    const [pageState, setPageState] = useState({ items: [], total: 0, pageCount: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [filterStatus, setFilterStatus] = useState("PENDING");
    const [filterDelta, setFilterDelta] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [filterSearch, setFilterSearch] = useState("");
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [busy, setBusy] = useState(false);
    const selectAllRef = useRef(null);

    const fetchData = async () => {
        setIsLoading(true);
        const response = await getAkfaMappings(token, {
            priceStatus: filterStatus,
            minDelta: filterDelta,
            search: filterSearch,
            page: page + AKFA_PAGE_BASE,
            size: AKFA_PAGE_SIZE,
        });
        if (response.success) {
            setPageState(toPageState(response.data, AKFA_PAGE_SIZE));
        } else {
            showError(response, t("akfaIntegrationPage.common.load_error"));
        }
        setIsLoading(false);
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filterStatus, filterDelta, filterSearch]);

    useEffect(() => {
        setSelectedIds(new Set());
    }, [page, filterStatus, filterDelta, filterSearch]);

    const items = pageState.items;
    const selectable = items.filter((item) => item.priceStatus === "PENDING");
    const isAllSelected = selectable.length > 0 && selectable.every((item) => selectedIds.has(item.id));
    const isIndeterminate = !isAllSelected && selectable.some((item) => selectedIds.has(item.id));

    useEffect(() => {
        if (selectAllRef.current) selectAllRef.current.indeterminate = isIndeterminate;
    }, [isIndeterminate]);

    const handleSelectAll = () => {
        setSelectedIds(isAllSelected ? new Set() : new Set(selectable.map((item) => item.id)));
    };

    const handleSelectOne = (id) => {
        const next = new Set(selectedIds);
        next.has(id) ? next.delete(id) : next.add(id);
        setSelectedIds(next);
    };

    const changeFilter = (setter) => (e) => {
        setPage(0);
        setter(e.target.value);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(0);
        setFilterSearch(searchInput.trim());
    };

    const refresh = () => {
        setSelectedIds(new Set());
        fetchData();
        refreshSummary();
    };

    const confirm = (title, text) =>
        swal({ title, text, icon: "warning", buttons: { cancel: true, confirm: true } });

    const confirmForce = (count, text) =>
        confirm(t("akfaIntegrationPage.priceChanges.stale_confirm_title", { count }), text || t("akfaIntegrationPage.priceChanges.stale_confirm_text"));

    const handleApproveOne = async (item) => {
        if (!(await confirm(t("akfaIntegrationPage.priceChanges.approve_confirm_title"), t("akfaIntegrationPage.priceChanges.approve_confirm_text", { count: 1 })))) {
            return;
        }
        setBusy(true);
        let response = await approveAkfaMappingPrice(token, item.id, false);
        // stale=true bo'lsa server 409 qaytaradi — force=true bilan qayta yuboriladi
        if (!response.success && response.status === 409) {
            if (!(await confirmForce(1, response.message))) {
                setBusy(false);
                return;
            }
            response = await approveAkfaMappingPrice(token, item.id, true);
        }
        setBusy(false);
        if (response.success) {
            showSuccess(t("akfaIntegrationPage.priceChanges.approve_success", { count: 1 }));
        } else {
            showError(response, t("akfaIntegrationPage.common.action_error"));
        }
        refresh();
    };

    const reportBulkResult = (data, successKey) => {
        const processed = data?.processed || [];
        const skipped = data?.skipped || [];
        if (processed.length > 0) showSuccess(t(successKey, { count: processed.length }));
        skipped.forEach((s) => toast.warn(`#${s.id}: ${s.reason}`, { position: "bottom-right", autoClose: 6000 }));
    };

    const handleApproveSelected = async () => {
        const ids = [...selectedIds];
        if (!(await confirm(t("akfaIntegrationPage.priceChanges.approve_confirm_title"), t("akfaIntegrationPage.priceChanges.approve_confirm_text", { count: ids.length })))) {
            return;
        }
        setBusy(true);
        const response = await bulkApproveAkfaMappingPrice(token, { ids, force: false });
        if (!response.success) {
            setBusy(false);
            showError(response, t("akfaIntegrationPage.common.action_error"));
            refresh();
            return;
        }
        const staleIds = new Set(items.filter((item) => item.stale).map((item) => item.id));
        const skipped = response.data?.skipped || [];
        const staleSkipped = skipped.filter((s) => staleIds.has(s.id));
        reportBulkResult(
            { processed: response.data?.processed, skipped: skipped.filter((s) => !staleIds.has(s.id)) },
            "akfaIntegrationPage.priceChanges.approve_success"
        );
        if (staleSkipped.length > 0 && (await confirmForce(staleSkipped.length))) {
            const forceResponse = await bulkApproveAkfaMappingPrice(token, {
                ids: staleSkipped.map((s) => s.id),
                force: true,
            });
            if (forceResponse.success) {
                reportBulkResult(forceResponse.data, "akfaIntegrationPage.priceChanges.approve_success");
            } else {
                showError(forceResponse, t("akfaIntegrationPage.common.action_error"));
            }
        }
        setBusy(false);
        refresh();
    };

    const handleReject = async (ids) => {
        if (!(await confirm(t("akfaIntegrationPage.priceChanges.reject_confirm_title"), t("akfaIntegrationPage.priceChanges.reject_confirm_text", { count: ids.length })))) {
            return;
        }
        setBusy(true);
        if (ids.length === 1) {
            const response = await rejectAkfaMappingPrice(token, ids[0]);
            if (response.success) {
                showSuccess(t("akfaIntegrationPage.priceChanges.reject_success", { count: 1 }));
            } else {
                showError(response, t("akfaIntegrationPage.common.action_error"));
            }
        } else {
            const response = await bulkRejectAkfaMappingPrice(token, { ids });
            if (response.success) {
                reportBulkResult(response.data, "akfaIntegrationPage.priceChanges.reject_success");
            } else {
                showError(response, t("akfaIntegrationPage.common.action_error"));
            }
        }
        setBusy(false);
        refresh();
    };

    return (
        <div className="py-4">
            <div className="mb-4 flex flex-wrap items-center gap-3">
                <select value={filterStatus} onChange={changeFilter(setFilterStatus)} className={SELECT_CLASS}>
                    {PRICE_STATUSES.map((s) => (
                        <option key={s} value={s}>
                            {t(`akfaIntegrationPage.priceStatus.${s}`)}
                        </option>
                    ))}
                </select>

                <select value={filterDelta} onChange={changeFilter(setFilterDelta)} className={SELECT_CLASS}>
                    <option value="">{t("akfaIntegrationPage.priceChanges.filter_delta_all")}</option>
                    {MIN_DELTA_OPTIONS.map((d) => (
                        <option key={d} value={d}>
                            |Δ| ≥ {d}%
                        </option>
                    ))}
                </select>

                <form onSubmit={handleSearch} className="flex gap-2">
                    <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder={t("akfaIntegrationPage.common.search_placeholder")}
                        className={SELECT_CLASS}
                    />
                    <button type="submit" className="px-4 py-2 bg-gray-700 text-white text-sm rounded-md hover:bg-gray-600">
                        {t("akfaIntegrationPage.common.search")}
                    </button>
                </form>

                {selectedIds.size > 0 && (
                    <div className="ml-auto flex items-center gap-3">
                        <span className="text-sm text-gray-600">
                            {t("akfaIntegrationPage.common.selected_count", { count: selectedIds.size })}
                        </span>
                        <button
                            onClick={handleApproveSelected}
                            disabled={busy}
                            className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-500 disabled:opacity-50"
                        >
                            {t("akfaIntegrationPage.priceChanges.approve_selected")}
                        </button>
                        <button
                            onClick={() => handleReject([...selectedIds])}
                            disabled={busy}
                            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-500 disabled:opacity-50"
                        >
                            {t("akfaIntegrationPage.priceChanges.reject_selected")}
                        </button>
                    </div>
                )}
            </div>

            {isLoading ? (
                <Loading />
            ) : (
                <>
                    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-300">
                            <thead className="text-white bg-gray-700">
                                <tr>
                                    <th className="py-3 pl-4 pr-2 w-10">
                                        {selectable.length > 0 && (
                                            <input
                                                ref={selectAllRef}
                                                type="checkbox"
                                                checked={isAllSelected}
                                                onChange={handleSelectAll}
                                                className="h-4 w-4 rounded border-gray-400 text-indigo-600 cursor-pointer"
                                            />
                                        )}
                                    </th>
                                    <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">
                                        {t("akfaIntegrationPage.priceChanges.col_product")}
                                    </th>
                                    <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">
                                        {t("akfaIntegrationPage.priceChanges.col_external")}
                                    </th>
                                    <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">
                                        {t("akfaIntegrationPage.priceChanges.col_group")}
                                    </th>
                                    <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">
                                        {t("akfaIntegrationPage.priceChanges.col_current_price")}
                                    </th>
                                    <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">
                                        {t("akfaIntegrationPage.priceChanges.col_new_price")}
                                    </th>
                                    <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">Δ</th>
                                    <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">
                                        {t("akfaIntegrationPage.common.status_label")}
                                    </th>
                                    <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">
                                        {t("akfaIntegrationPage.priceChanges.col_since")}
                                    </th>
                                    <th className="relative py-3 pl-3 pr-4">
                                        <span className="sr-only">{t("akfaIntegrationPage.common.actions")}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-300">
                                {items.length > 0 ? (
                                    items.map((item) => {
                                        const isPending = item.priceStatus === "PENDING";
                                        return (
                                            <tr
                                                key={item.id}
                                                className={`hover:bg-gray-50 ${selectedIds.has(item.id) ? "bg-indigo-50" : ""}`}
                                            >
                                                <td className="py-2 pl-4 pr-2">
                                                    {isPending && (
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedIds.has(item.id)}
                                                            onChange={() => handleSelectOne(item.id)}
                                                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 cursor-pointer"
                                                        />
                                                    )}
                                                </td>
                                                <td className="px-2 py-2 text-sm text-gray-700">
                                                    <div className="font-medium text-gray-900">
                                                        {item.productName}
                                                        {item.article && (
                                                            <span className="text-gray-400 font-normal"> ({item.article})</span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-gray-500">{item.textureName}</div>
                                                    {item.stale && (
                                                        <div className="text-xs text-yellow-700 mt-0.5">
                                                            ⚠ {t("akfaIntegrationPage.priceChanges.stale_badge")}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-2 py-2 text-sm text-gray-700">{item.akfaProductName}</td>
                                                <td className="px-2 py-2 text-sm">
                                                    <span className="bg-gray-100 text-gray-500 rounded-md px-2 py-0.5 text-xs whitespace-nowrap">
                                                        {item.groupName || "—"}
                                                    </span>
                                                </td>
                                                <td className="px-2 py-2 text-sm text-gray-700">
                                                    {formatPrice(item.pendingBasePrice, item.pendingBaseCurrencySymbol)}
                                                </td>
                                                <td className="px-2 py-2 text-sm font-medium text-indigo-700">
                                                    {formatPrice(item.pendingPrice, item.pendingCurrencySymbol)}
                                                </td>
                                                <td className="px-2 py-2 text-sm">
                                                    <DeltaBadge delta={item.priceDeltaPercent} />
                                                </td>
                                                <td className="px-2 py-2 text-sm">
                                                    <span
                                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                            PRICE_STATUS_STYLES[item.priceStatus] || "bg-gray-100 text-gray-800"
                                                        }`}
                                                    >
                                                        {t(`akfaIntegrationPage.priceStatus.${item.priceStatus}`, {
                                                            defaultValue: item.priceStatus,
                                                        })}
                                                    </span>
                                                    {item.priceError && (
                                                        <div className="text-xs text-red-700 mt-1">{item.priceError}</div>
                                                    )}
                                                    {item.lastAppliedAt && (
                                                        <div className="text-xs text-gray-400 mt-1">
                                                            {formatPrice(item.lastAppliedPrice, item.pendingCurrencySymbol)} ·{" "}
                                                            {formatDateTime(item.lastAppliedAt)}
                                                            {item.lastAppliedBy ? ` · ${item.lastAppliedBy}` : ""}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-2 py-2 text-sm text-gray-700">
                                                    {formatDateTime(item.pendingSince)}
                                                </td>
                                                <td className="relative py-2 text-right text-sm font-medium pr-4">
                                                    {isPending && (
                                                        <div className="flex justify-end gap-2">
                                                            <button
                                                                onClick={() => handleApproveOne(item)}
                                                                disabled={busy}
                                                                className="px-3 py-1 bg-green-600 text-white rounded-md text-xs hover:bg-green-500 disabled:opacity-50"
                                                            >
                                                                {t("akfaIntegrationPage.priceChanges.approve")}
                                                            </button>
                                                            <button
                                                                onClick={() => handleReject([item.id])}
                                                                disabled={busy}
                                                                className="px-3 py-1 bg-red-600 text-white rounded-md text-xs hover:bg-red-500 disabled:opacity-50"
                                                            >
                                                                {t("akfaIntegrationPage.priceChanges.reject")}
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={10} className="py-8 text-center text-sm text-gray-500">
                                            {t("akfaIntegrationPage.priceChanges.empty")}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {pageState.pageCount > 1 && (
                        <Pagination
                            handlePageClick={(e) => setPage(e.selected)}
                            pageCount={pageState.pageCount}
                            forcePage={page}
                        />
                    )}
                </>
            )}
        </div>
    );
}
